import {
  Compass,
  Anchor,
  Users,
  Sparkle,
} from "lucide-react";
import { useReveal } from "../../hooks/useReveal";

const OUTCOMES = [
  {
    icon: Sparkle,
    label: "Understand yourself",
    detail: "see what's really going on underneath",
  },
  {
    icon: Anchor,
    label: "Steadier with emotion",
    detail: "less at the mercy of how you feel",
  },
  {
    icon: Users,
    label: "Relate differently",
    detail: "without the same old patterns",
  },
  {
    icon: Compass,
    label: "Direction & choice",
    detail: "a sense of where you're going",
  },
];

export const Approach = () => {
  const ref = useReveal();

  return (
    <section
      id="approach"
      data-testid="approach-section"
      className="bg-[#F3F0EA]"
    >
      <div className="mx-auto max-w-[1320px] px-6 md:px-10 lg:px-16 py-20 md:py-20 lg:py-24">
        <div ref={ref} className="reveal">
          <div className="max-w-4xl">
            <p className="eyebrow-rule text-[11px] uppercase tracking-[0.28em] text-[#4A4D4A] mb-6">
              <span>01 · My Approach</span>
            </p>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-[3rem] leading-[1.1] tracking-[-0.02em] text-[#1A1C1A] font-medium">
              Therapy that{" "}
              <em className="italic font-normal text-[#1B2E26]">stays with you</em>.
            </h2>
            <p className="mt-8 text-lg md:text-xl leading-relaxed text-[#4A4D4A] max-w-2xl font-sans-body">
              I&rsquo;m Dr. Chandril Ghosh &mdash; a clinical psychologist
              helping adults move past short-term symptom relief toward a
              deeper sense of safety, self-trust, and emotional security.
            </p>
          </div>

          {/* Outcome chips */}
          <div
            data-testid="approach-outcomes"
            className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#DCD7CE] bg-[#FDFBF7]"
          >
            {OUTCOMES.map((o) => {
              const Icon = o.icon;
              return (
                <div
                  key={o.label}
                  className="p-7 md:p-9 border-r border-b border-[#DCD7CE] hover:bg-[#F3F0EA] transition-colors"
                >
                  <Icon size={22} strokeWidth={1.4} className="text-[#1B2E26]" />
                  <p className="mt-6 font-serif-display text-xl md:text-2xl text-[#1A1C1A] leading-snug">
                    {o.label}
                  </p>
                  <p className="mt-2 text-sm text-[#4A4D4A] leading-relaxed">
                    {o.detail}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Brief NHS / private contrast */}
          <div
            data-testid="approach-contrast"
            className="mt-12 md:mt-14 grid grid-cols-1 md:grid-cols-2 border border-[#DCD7CE] bg-[#FDFBF7]"
          >
            <div className="p-7 md:p-9 border-b md:border-b-0 md:border-r border-[#DCD7CE]">
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#4A4D4A]">
                NHS &middot; brief therapy
              </p>
              <p className="mt-3 font-serif-display text-xl md:text-2xl text-[#1A1C1A] leading-snug">
                Session cap. Set protocol.
              </p>
              <p className="mt-3 text-sm text-[#4A4D4A] leading-relaxed">
                Symptoms often ease &mdash; the pattern underneath can stay.
              </p>
            </div>
            <div className="p-7 md:p-9 bg-[#1B2E26] text-[#FDFBF7]">
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#E9E4DA]/80">
                Working privately
              </p>
              <p className="mt-3 font-serif-display text-xl md:text-2xl leading-snug">
                No cap. CBT, ACT &amp; CFT, tailored.
              </p>
              <p className="mt-3 text-sm text-[#E9E4DA]/85 leading-relaxed">
                We&rsquo;ll name what a good outcome looks like &mdash; then
                work toward it.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
